<template>
  <QuickStatsPageShell
    title="League Trends"
    description="League trend rows grouped by league, market, and timeframe from public Paulie data."
    :status-message="statusMessage"
  >
    <template #hero-right>
      <QuickStatsProDashboardBanner />
    </template>

    <section class="data-toolbar">
      <label class="filter-field" for="league">
        <span class="filter-label">League</span>
        <select id="league" v-model="selectedLeague" class="filter-select">
          <option value="All">All</option>
          <option v-for="league in leagueOptions" :key="league" :value="league">{{ league }}</option>
        </select>
      </label>

      <label class="filter-field" for="timeframe">
        <span class="filter-label">Timeframe</span>
        <select id="timeframe" v-model="selectedTimeframe" class="filter-select">
          <option value="All">All</option>
          <option v-for="timeframe in timeframeOptions" :key="timeframe" :value="timeframe">
            {{ formatTimeframeLabel(timeframe, selectedLeague === 'All' ? '' : selectedLeague) }}
          </option>
        </select>
      </label>
    </section>

    <QuickStatsLoadingSkeleton v-if="loading" label="Loading league trends" />
    <div v-else-if="groups.length === 0" class="state-text">
      <strong><em>No free data available at this time. Subscribe in-app for full access.</em></strong>
    </div>

    <section v-else class="group-grid">
      <article v-for="group in groups" :key="group.key" class="card group-card">
        <header class="group-head">
          <div class="league-title-wrap">
            <QuickStatsLeagueMark :league="group.league" :logo-url="group.leagueLogo" class="league-logo" />
            <h2>{{ group.league }}</h2>
          </div>
          <p class="meta">{{ formatMarketLabel(group.market) }} · {{ formatTimeframeLabel(group.timeframe, group.league) }}</p>
          <p class="updated-at">Updated {{ formatDateTime(group.latestUpdatedAt) }}</p>
        </header>

        <div class="data-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Record</th>
                <th>Win Rate</th>
                <th>ROI</th>
                <th v-if="group.showPushes" class="hide-md">Pushes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in group.rows" :key="row.id">
                <td data-label="Category">{{ row.categoryLabel }}</td>
                <td data-label="Record">{{ row.record || 'N/A' }}</td>
                <td data-label="Win Rate">{{ formatPercent(row.winRate) }}</td>
                <td data-label="ROI">{{ formatPercent(row.roi) }}</td>
                <td v-if="group.showPushes" class="hide-md" data-label="Pushes">{{ nullable(row.pushes) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </section>

    <p class="cross-link">
      Browse team-level trends in
      <router-link to="/quick-stats/team-rankings">Team Rankings</router-link>
      and lines in
      <router-link to="/quick-stats/game-previews">Game Previews</router-link>.
    </p>
  </QuickStatsPageShell>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import QuickStatsPageShell from '@/components/QuickStatsPageShell.vue'
import QuickStatsLeagueMark from '@/components/QuickStatsLeagueMark.vue'
import QuickStatsLoadingSkeleton from '@/components/QuickStatsLoadingSkeleton.vue'
import QuickStatsProDashboardBanner from '@/components/QuickStatsProDashboardBanner.vue'
import { formatDateTime, formatMarketLabel, formatPercent, formatTimeframeLabel, sortTimeframes } from '@/lib/quickStats/format'
import { getLeagueTrends } from '@/lib/quickStats/service'

const loading = ref(true)
const rows = ref([])
const statusMessage = ref('')
const selectedLeague = ref('All')
const selectedTimeframe = ref('All')

const leagueOptions = computed(() => {
  return Array.from(new Set(rows.value.map((row) => row.league))).sort((a, b) => a.localeCompare(b))
})

const timeframeOptions = computed(() => {
  const scopedRows = selectedLeague.value === 'All'
    ? rows.value
    : rows.value.filter((row) => row.league === selectedLeague.value)

  return sortTimeframes(
    Array.from(new Set(scopedRows.map((row) => row.timeframe))),
    selectedLeague.value === 'All' ? '' : selectedLeague.value
  )
})

const filteredRows = computed(() => {
  return rows.value.filter((row) => {
    if (selectedLeague.value !== 'All' && row.league !== selectedLeague.value) return false
    if (selectedTimeframe.value !== 'All' && row.timeframe !== selectedTimeframe.value) return false
    return true
  })
})

const groups = computed(() => {
  const grouped = new Map()

  filteredRows.value.forEach((row) => {
    const key = `${row.league}|${row.market}|${row.timeframe}`
    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        league: row.league,
        leagueLogo: row.leagueLogo,
        market: row.market,
        timeframe: row.timeframe,
        rows: []
      })
    }

    grouped.get(key).rows.push(row)
  })

  return Array.from(grouped.values())
    .sort((a, b) => {
      if (a.league !== b.league) return a.league.localeCompare(b.league)
      if (a.timeframe !== b.timeframe) return a.timeframe.localeCompare(b.timeframe)
      return a.market.localeCompare(b.market)
    })
    .map((group) => {
      const sortedRows = group.rows.slice().sort((a, b) => {
        const aRate = a.winRate === null ? -1 : a.winRate
        const bRate = b.winRate === null ? -1 : b.winRate
        if (aRate !== bRate) return bRate - aRate
        return a.categoryLabel.localeCompare(b.categoryLabel)
      })

      return {
        ...group,
        latestUpdatedAt: sortedRows.map((row) => row.updatedAt).filter(Boolean).sort().at(-1) || '',
        showPushes: sortedRows.some((row) => Number(row.pushes) > 0),
        rows: sortedRows
      }
    })
})

function nullable(value) {
  return value === null || value === undefined ? 'N/A' : value
}

onMounted(async () => {
  const result = await getLeagueTrends()
  rows.value = result.rows
  statusMessage.value = result.errorMessage
  loading.value = false
})
</script>

<style scoped>
.group-grid {
  display: grid;
  gap: 1rem;
}

.group-card {
  padding: 1rem;
}

.group-head {
  margin-bottom: 0.65rem;
}

.league-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.league-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.meta {
  margin-top: 0.3rem;
  color: var(--text-muted);
  font-size: 0.86rem;
}

.updated-at {
  margin-top: 0.2rem;
  color: var(--text-muted);
  font-size: 0.82rem;
}

.state-text {
  color: var(--text-muted);
}

.cross-link {
  margin-top: 1.1rem;
}

.cross-link a {
  color: var(--accent);
  text-decoration: none;
}
</style>
